<script setup>
import PreviewPanel from '@/components/SingleFileViewComponent.vue';
import ToastComponent from '@/components/ToastComponent.vue';
import { useAxios } from '@/hooks/useAxios';
import { computed, inject, ref, watch } from 'vue';
let BASE_URL = import.meta.env.VITE_APP_API_URL;

// Props 정의
const props = defineProps({
    modelValue: {
        type: [String, Number, File, Array, Object],
        default: ''
    },
    inputId: {
        type: String,
        default: ''
    },
    label: {
        type: String,
        default: ''
    },
    required: {
        type: Boolean,
        default: false
    },
    placeholder: {
        type: String,
        default: '파일을 선택하세요.' //'파일을 선택하세요. (PDF, JPEG, JPG, PNG, GIF, BMP 파일만 업로드 가능합니다.)'
    },
    disabled: {
        type: Boolean,
        default: false
    },
    marginTop: {
        type: String,
        default: '0'
    },
    marginBottom: {
        type: String,
        default: '0'
    },
    marginLeft: {
        type: String,
        default: '0'
    },
    marginRight: {
        type: String,
        default: '0'
    },
    labelRightmargin: {
        type: String,
        default: '2rem'
    },
    visible: {
        type: Boolean,
        default: true
    },
    button: {
        type: String,
        default: ''
    },
    fileId: {
        type: String,
        default: ''
    },
    filename: {
        type: String,
        default: ''
    },
    originalname: {
        type: String,
        default: ''
    },
    errmsg: {
        type: String,
        default: ''
    },
    readonly: {
        type: Boolean,
        default: false
    }
    // updateComponentState: {
    //     type: [Array, Function],
    //     default: () => []
    // }
});

// Emits 정의
const emit = defineEmits(['update:modelValue', 'input', 'change', 'click']);
const allowedFileTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/bmp'];
const allowedExtensions = ['.pdf', '.jpeg', '.jpg', '.png', '.gif', '.bmp'];
const fileUploads = inject('fileUploads');
const handleFileUpload = inject('handleFileUpload');
const fileIdRef = ref(props.fileId);
const filenameRef = ref(props.filename);
const originalnameRef = ref(props.originalname);
const errorstate = ref(false);
const toastComponentRef = ref(null);
const ValidationMsg = ref('');
const isReadonly = computed(() => props.readonly);
const errMessage = ref(props.errmsg);
const previewFile = ref({
    url: '',
    type: ''
});
const downloadFile = ref({
    url: ''
});

// 내부 상태 관리
const selectedFiles = ref([]); // 선택된 파일
const selectedFile = ref([]); // 선택된 파일
const fileInput = ref(null);

const triggerFileInput = () => {
    if (isReadonly.value || errorstate.value) return;
    if (selectedFiles.value.length === 0) {
        fileInput.value.click();
    }
};

const resetFiles = (event) => {
    if (!isReadonly) return;
    if (filenameRef.value === '' || errorstate.value) {
        clearFiles();
    } else {
        handleFileUpload('remove', props.inputId, filenameRef.value);
        selectedFile.value.delete = true;
    }
};

const clearFiles = () => {
    selectedFiles.value = [];
    selectedFile.value = [];
    ValidationMsg.value = '';
    errMessage.value = '';
    errorstate.value = false;
    emit('update:modelValue', '');
    if (fileInput.value) {
        fileInput.value.value = '';
    }
    fileIdRef.value = '';
    filenameRef.value = '';
    originalnameRef.value = '';
};

const handleChange = (event) => {
    const files = Array.from(event.target.files);
    const file = files[0];
    filenameRef.value = '';
    fileIdRef.value = '';
    originalnameRef.value = '';
    ValidationMsg.value = '';
    errMessage.value = '';
    errorstate.value = false;
    if (file) {
        const fileExtension = '.' + file.name.split('.').pop().toLowerCase();
        const isValidType = allowedFileTypes.includes(file.type) || allowedExtensions.includes(fileExtension);

        if (!isValidType) {
            if (toastComponentRef.value) {
                toastComponentRef.value.showToast('error', '알림', '허용되지 않는 파일 형식입니다. PDF, JPEG, JPG, PNG, GIF, BMP 파일만 업로드 가능합니다.');
            }
            event.target.value = ''; // 파일 선택 초기화
            return;
        }

        selectedFiles.value = [file]; // 파일선택

        selectedFile.value.push({
            file,
            fileId: props.fileId || '',
            filename: props.filename || '',
            name: file.name,
            size: (file.size / 1024).toFixed(2),
            uploaded: false,
            delete: false
        });

        emit('change', ['change', props.inputId, file]);
        handleFileUpload('upload', props.inputId, file);
    } else {
        // selectedFiles.value = [];
        // emit('change', ['reset', props.inputId]);
    }
};

const fileInfo = computed(() => {
    if (filenameRef.value) {
        return {
            name: originalnameRef.value,
            showSize: false,
            showDownload: true
        };
    } else if (selectedFiles.value.length > 0) {
        const file = selectedFiles.value[0];
        return {
            name: file.name,
            size: (file.size / 1024).toFixed(2),
            showSize: true,
            showDownload: false
        };
    }
    return null;
});

const setFile = () => {
    if (filenameRef.value == '') return;
    errorstate.value = false;
    try {
        //const url = `/upload/images/${fileIdRef.value}`; // fileId로 다건 조회
        const url = `/upload/images/filename/${filenameRef.value}`;
        const config = {
            method: 'get'
        };
        const { data, error, execute } = useAxios(url, config, {
            immediate: false,
            onSuccess(response) {
                //console.log(props.inputId, ' ', response.data.data);
                fileIdRef.value = response.data.data[0].attachment_id;
                //filenameRef.value = response.data.data[0].attachment_filename;
                originalnameRef.value = response.data.data[0].original_filename;
                ValidationMsg.value = '';
                errMessage.value = '';
                errorstate.value = false;
                emit('update:modelValue', fileIdRef.value);

                // selectedFiles에 추가
                selectedFile.value = {
                    fileId: response.data.data[0].attachment_id,
                    filename: response.data.data[0].attachment_filename,
                    name: response.data.data[0].original_filename,
                    uploaded: true,
                    delete: false
                };

                //emit('update:modelValue', { fileId: fileIdRef.value, filename: filenameRef.value, originalname: originalnameRef.value });
            },
            onError(err) {
                console.error('Error:', err.message);
                errorstate.value = true;
                originalnameRef.value = '파일을 찾을 수 없습니다.';
                ///clearFiles();
            }
        });

        execute();
    } catch (error) {
        console.error('파일 조회 오류 발생:', error);
    }
};

// watch(
//     () => fileUploads.value[props.inputId],
//     (newResult) => {
//         if (newResult && newResult.option === 'upload') {
//             fileIdRef.value = newResult.fileId;
//             filenameRef.value = newResult.filename;
//             originalnameRef.value = newResult.originalname;
//             setFile();
//         }

//         if (newResult && newResult.option === 'remove') {
//             clearFiles();
//         }
//     }
//     // { deep: true }
// );

watch(
    () => props.fileId,
    (newVal) => {
        fileIdRef.value = newVal;
        setFile();
    },
    { immediate: true, deep: true }
);

watch(
    () => props.filename,
    (newVal) => {
        filenameRef.value = newVal;
        setFile();
    },
    { immediate: true, deep: true }
);

watch(
    () => props.errmsg,
    (newVal) => {
        ValidationMsg.value = newVal || '';
        errMessage.value = newVal || '';
    },
    { immediate: true, deep: true }
);

const handleFileClick = (event) => {
    //console.log('handleFileClick', fileIdRef.value, ' , ', originalnameRef.value);
    if (filenameRef.value === '') return;

    try {
        const url = `/view/${filenameRef.value}`;
        const config = {
            method: 'get'
        };
        const { data, error, execute } = useAxios(url, config, {
            immediate: false,
            onSuccess(response) {
                // if (response.headers['content-type'] === 'application/pdf') {
                // } else {
                previewFile.value = {
                    url: `${BASE_URL}/view/${filenameRef.value}`,
                    type: response.headers['content-type'],
                    downurl: `${BASE_URL}/download/${filenameRef.value}`
                };
                // }
            },
            onError(err) {
                console.error('Error:', err.message);
            }
        });

        execute();
    } catch (error) {
        console.error('파일 업로드 중 오류 발생:', error);
    }

    // const url = 'http://192.168.123.80:8080/view/23d6-83a7-f02a-be93-68c6';
    // const windowFeatures = 'left=100,top=100,width=800,height=600,popup';
    // window.open(url, 'Preview', windowFeatures);
};

const closePreview = () => {
    previewFile.value = {
        data: '',
        type: ''
    };
};

const downloadFiles = (event) => {
    //console.log('downloadFiles', fileIdRef.value, ' , ', originalnameRef.value);

    const fileUrl = `${BASE_URL}/download/${filenameRef.value}`;
    window.open(fileUrl, '_self');
    setTimeout(() => {
        downloadFile.value = { url: '' };
    }, 300);
    // downloadFile.value = {
    //     url: `${BASE_URL}/download/${filenameRef.value}`
    //     //type: 'application/pdf' // 필요에 따라 타입을 동적으로 변경 가능
    // };
    // window.open(`${BASE_URL}/download/${filenameRef.value}`, '_blank'); // 단건
    // window.open(`${BASE_URL}/download/files/${fileIdRef.value}`, '_blank'); // 다중건

    // try {
    //     const url = `/download/files/${filenameRef.value}`;
    //     const config = {
    //         method: 'get'
    //     };
    //     useAxios.setHeader('GID-AUTH', `${accessToken}`)
    //     const { data, error, execute } = useAxios(url, config, {
    //         immediate: false,
    //         onSuccess(response) {},
    //         onError(err) {
    //             console.error('Error:', err.message);
    //         }
    //     });

    //     execute();
    // } catch (error) {
    //     console.error('', error);
    // }
};

const truncateFileName = (fileName) => {
    if (fileName.length < 18) return fileName;
    return fileName.substr(0, 17) + '...';
};

defineExpose({
    handleChange,
    resetFiles,
    selectedFile
});
</script>

<template>
    <ToastComponent ref="toastComponentRef" />
    <PreviewPanel v-if="previewFile.url" :fileUrl="previewFile.url" :fileType="previewFile.type" :downurl="previewFile.downurl" @close="closePreview" />

    <div v-if="downloadFile.url" class="file-preview-container">
        <object :data="downloadFile.url" height="0"><a :href="downloadFile.url" target="_self">여기</a>를 클릭하여 다운로드하세요.</object>
    </div>

    <div v-if="visible">
        <div class="header-container" :style="{ marginTop, marginBottom, marginLeft, marginRight }">
            <label :class="['label-font mb-1 mx-1', required ? 'label-required' : 'text-gray-700 dark:text-gray-300']" @click.prevent>
                {{ label }}
                <span v-if="required" class="required-asterisk">*</span>
                <span v-if="ValidationMsg" class="validation-message">{{ ValidationMsg }}</span>
            </label>
            <div class="select-count">
                <button @click="triggerFileInput" class="upload-button" :class="{ disabled: disabled }">파일 선택</button>
            </div>
        </div>

        <div class="relative flex items-center w-full">
            <input ref="fileInput" type="file" :id="inputId" class="hidden" :required="required" :disabled="disabled" @change="handleChange" accept=".pdf,.jpeg,.jpg,.png,.gif,.bmp" />
            <div class="relative w-full">
                <div
                    :class="[
                        'block w-full p-3 ps-5 input-font text-gray-900 border border-gray-300 rounded-lg bg-gray-60 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500 pr-6 min-h-[42px] overflow-y-auto max-h-32',
                        { 'file-selected': fileInfo, 'cursor-default': !fileInfo, 'cursor-default': fileInfo, 'error-border': ValidationMsg }
                    ]"
                >
                    <template v-if="fileInfo">
                        <div class="flex items-center justify-between file-info-container" @click.stop="handleFileInfoClick">
                            <span :class="[{ 'file-info-hover': fileInfo && fileInfo.showDownload && !errorstate }]" :title="fileInfo.name" @click.stop="handleFileClick">
                                {{ truncateFileName(fileInfo.name) }}
                                <template v-if="fileInfo.showSize"> ({{ fileInfo.size }} KB) </template>
                                <template v-if="fileInfo.showDownload && !errorstate">
                                    <button class="down-button" @click.stop="downloadFiles">
                                        <i class="pi pi-download"></i>
                                    </button>
                                </template>
                                <template v-if="!isReadonly.value">
                                    <button class="remove-button" @click.stop="resetFiles">
                                        <span v-if="selectedFile.delete">
                                            <i class="pi pi-undo"></i>
                                        </span>
                                        <span v-else><i class="pi pi-times"></i></span>
                                    </button>
                                </template>
                            </span>
                        </div>
                    </template>
                    <span v-else class="text-gray-400" :class="[{ 'cursor-pointer': !isReadonly }]" @click="triggerFileInput">{{ placeholder }}</span>
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped>
.label-font {
    font-family: 'SCoreDream600';
    height: 1.3rem;
}

.input-font {
    font-family: 'SCoreDream400';
    font-size: 0.9rem;
}

.select-count {
    font-family: 'SCoreDream400';
    font-size: 0.875rem;
    opacity: 0.9;
    display: flex;
    align-items: center;
    /* justify-content: flex-end; */
    margin-right: 0.3rem;
}

.header-container {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 0.25rem;
}

.validation-message {
    color: #f87171;
    font-size: 0.75rem;
    margin-left: 0.5rem;
}

.error-border {
    border: 1px solid red !important;
    border-radius: 0.5rem;
}

.required-asterisk {
    color: #f87171; /* 빨간색 */
    margin-left: 0.2rem;
    position: relative;
    top: -0.2rem;
}

.header-container {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 0.25rem;
}

input:focus {
    border-color: #0038ea;
    outline: none;
}

button {
    font-family: 'SCoreDream400';
    font-size: 0.75rem;
    transition: color 0.3s ease;
    border-radius: 6px;
    color: #0038ea;
    margin-left: 1rem;
    cursor: pointer;
}

button:focus {
    outline: none;
}

.remove-button {
    margin-left: 0.3rem;
    opacity: 0.7;
    background-color: transparent;
    color: #f87171;
}

.remove-button:hover {
    color: red;
    opacity: 0.9;
}

.pi-times {
    font-size: 0.7rem;
    padding: 0 0 0.35rem 0.4rem;
}

.file-selected {
    /* background-color: #f0f0f0; */
}

.file-info-container {
    cursor: default;
    font-family: 'SCoreDream400';
    font-size: 0.85rem;
}

.file-info-hover:hover {
    cursor: pointer;
    text-decoration: underline;
    color: #0038ea;
}

.upload-button {
    /* padding: 0.5rem 1rem; */
    /* background-color: white; */
    color: #0038ea;
    /* border: 0.1rem solid #d1d5db; */
    /* border-radius: 0.5rem; */
    cursor: pointer;
    font-family: 'SCoreDream500';
    font-size: 0.9rem;
    text-align: center;
    transition: background-color 0.2s;
}

.upload-button:hover {
    font-family: 'SCoreDream600';
}

.upload-button.disabled {
    opacity: 0.5;
    cursor: not-allowed;
}
</style>
